import React from 'react';
import { View, Text, StyleSheet, Platform, Pressable } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DLOButton from './DLOButton';

interface DLOCreateScheduleHeaderProps {
  forecastedKCPH?: number;
  targetKCPH?: number;
  scheduledKCPH?: number;
  estimatedKCPH?: number;
  actualKCPH?: number;
  adjustedRecommendedKCPH?: number;
  crewedKCPH?: number;
  activeTab?: 'schedule' | 'actual';
  onScheduleSettings?: () => void;
  onSubmit?: () => void;
  onPrint?: () => void;
  onSave?: () => void;
  onNavigate?: (screen: string) => void;
  // Loading states for disabling buttons
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
  isScheduleSettingsDisabled?: boolean;
  isPrintDisabled?: boolean;
  isSaveDisabled?: boolean;
  isSaveLoading?: boolean;
  hasUnsavedChanges?: boolean;
}

// Schedule Settings Icon
const ScheduleSettingsIcon = ({ color = "#005CBC" }: { color?: string }) => (
  <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
    <Path 
      d="M12.49 2C6.97 2 2.5 6.48 2.5 12C2.5 17.52 6.97 22 12.49 22C18.02 22 22.5 17.52 22.5 12C22.5 6.48 18.02 2 12.49 2ZM12.5 20C8.08 20 4.5 16.42 4.5 12C4.5 7.58 8.08 4 12.5 4C16.92 4 20.5 7.58 20.5 12C20.5 16.42 16.92 20 12.5 20ZM13 7H11.5V13L16.75 16.15L17.5 14.92L13 12.25V7Z" 
      fill={color}
    />
  </Svg>
);

// Submit Icon
const SubmitIcon = ({ color = "white" }: { color?: string }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M9 16H15V10H19L12 3L5 10H9V16ZM12 5.83L14.17 8H13V14H11V8H9.83L12 5.83ZM5 18H19V20H5V18Z" 
      fill={color} 
    />
  </Svg>
);

// Print Icon
const PrintIcon = ({ color = "#005CBC" }: { color?: string }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M19 8H5C3.34 8 2 9.34 2 11V17H6V21H18V17H22V11C22 9.34 20.66 8 19 8ZM16 19H8V14H16V19ZM19 12C18.45 12 18 11.55 18 11C18 10.45 18.45 10 19 10C19.55 10 20 10.45 20 11C20 11.55 19.55 12 19 12ZM18 3H6V7H18V3Z" 
      fill={color}
    />
  </Svg>
);

// Save Icon
const SaveIcon = ({ color = "#005CBC" }: { color?: string }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM19 19H5V5H16.17L19 7.83V19ZM12 12C10.34 12 9 13.34 9 15C9 16.66 10.34 18 12 18C13.66 18 15 16.66 15 15C15 13.34 13.66 12 12 12ZM6 6H15V10H6V6Z" 
      fill={color}
    />
  </Svg>
);

const DLOCreateScheduleHeader: React.FC<DLOCreateScheduleHeaderProps> = ({
  forecastedKCPH = 52,
  targetKCPH = 51.31,
  scheduledKCPH = 52,
  estimatedKCPH = 0,
  actualKCPH = 0,
  adjustedRecommendedKCPH = 0,
  crewedKCPH = 0,
  activeTab = 'schedule',
  onScheduleSettings,
  onSubmit,
  onPrint,
  onSave,
  onNavigate,
  isSubmitDisabled = false,
  isSubmitLoading = false,
  isScheduleSettingsDisabled = false,
  isPrintDisabled = false,
  isSaveDisabled = false,
  isSaveLoading = false,
  hasUnsavedChanges = false,
}) => {
  const [isPrintHovered, setIsPrintHovered] = React.useState(false);
  const [isSaveHovered, setIsSaveHovered] = React.useState(false);

  // const handleScheduleSettings = () => {
  //   if (onNavigate) {
  //     onNavigate('DLOScheduleSetting');
  //   } else if (onScheduleSettings) {
  //     onScheduleSettings();
  //   }
  // };

  return (
    <View
      style={[
        styles.headerContainer,
        activeTab === 'actual' && styles.actualTabContainer
      ]}
    >
      {/* KCPH Metrics Section */}
      <View style={activeTab === 'actual' ? styles.actualTabMetricsContainer : styles.metricsContainer}>
        {/* Schedule Tab: Show Recommended KCPH, Scheduled KCPH, Target KCPH */}
        {activeTab === 'schedule' && (
          <>
            {/* Recommended KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Recommended KCPH: {adjustedRecommendedKCPH}</Text>
              </View>
            </View>

            {/* Scheduled KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Scheduled KCPH: {scheduledKCPH}</Text>
              </View>
            </View>

            {/* Target KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Target KCPH: {targetKCPH}</Text>
              </View>
            </View>
          </>
        )}

        {/* Actual Tab: Show Estimated KCPH, Crewed KCPH, Target KCPH, Actual KCPH */}
        {activeTab === 'actual' && (
          <>
            {/* Estimated KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Updated Recommended KCPH: {estimatedKCPH}</Text>
              </View>
            </View>

            {/* Crewed KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Crewed KCPH: {crewedKCPH}</Text>
              </View>
            </View>
            
            {/* Target KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Target KCPH: {targetKCPH}</Text>
              </View>
            </View>

            {/* Actual KCPH */}
            <View style={styles.metricCard}>
              <View style={styles.metricContent}>
                <Text style={styles.metricText}>Actual KCPH: {actualKCPH}</Text>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Action Buttons Section */}
      <View style={activeTab === 'actual' ? styles.actualTabActionsContainer : styles.actionsContainer}>
        {/* Print Button */}
        <Pressable
          onHoverIn={() => setIsPrintHovered(true)}
          onHoverOut={() => setIsPrintHovered(false)}
          onPress={isPrintDisabled ? undefined : onPrint}
          disabled={isPrintDisabled}
        >
          <DLOButton
            text="Print"
            variant="secondary"
            size="large"
            iconLeading={<PrintIcon color={isPrintHovered ? "white" : "#005CBC"} />}
            onPress={onPrint}
            disabled={isPrintDisabled}
            style={styles.printButton}
          />
        </Pressable>

        {/* Schedule Settings Button */}
        <DLOButton
          text="Schedule Settings"
          variant="primary"
          size="large"
          iconLeading={<ScheduleSettingsIcon color="#FFFFFF" />}
          onPress={onScheduleSettings}
          disabled={isScheduleSettingsDisabled}
          style={styles.scheduleSettingsButton}
        />

        {/* Save Button */}
        <Pressable
          onHoverIn={() => setIsSaveHovered(true)}
          onHoverOut={() => setIsSaveHovered(false)}
          onPress={isSaveDisabled ? undefined : onSave}
          disabled={isSaveDisabled}
        >
          <DLOButton
            text="Save"
            variant="secondary"
            size="large"
            iconLeading={<SaveIcon color={isSaveHovered ? "white" : "#005CBC"} />}
            onPress={onSave}
            disabled={isSaveDisabled}
            isLoading={isSaveLoading}
            style={styles.saveButton}
          />
        </Pressable>

        {/* Submit Button */}
        <DLOButton
          text="Submit"
          variant="primary"
          size="large"
          iconLeading={<SubmitIcon color={isSubmitDisabled || isSubmitLoading ? "rgb(160, 160, 160)" : "white"} />}
          onPress={onSubmit}
          disabled={isSubmitDisabled}
          isLoading={isSubmitLoading}
          style={styles.submitButton}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'rgb(240, 245, 252)',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    flexWrap: 'wrap',
    gap: 16,
  },
  metricsContainer: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  actualTabMetricsContainer: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    width: '100%',
  },
  metricCard: {
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: {
      width: 4,
      height: 0,
    },
    shadowRadius: 24,
    elevation: 24,
    shadowOpacity: 1,
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: 6,
    minWidth: 120,
    maxWidth: 300,
    flexShrink: 1,
  },
  metricContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metricText: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
    color: '#005CBC',
    textAlign: 'left',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  actualTabActionsContainer: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    width: '100%',
  },
  printButton: {
    minWidth: 100,
  },
  scheduleSettingsButton: {
    minWidth: 120,
  },
  saveButton: {
    minWidth: 100,
  },
  submitButton: {
    minWidth: 100,
  },
  actualTabContainer: {
    flexDirection: 'column',
    gap: 16,
  },
});

export default DLOCreateScheduleHeader;
